// src/components/OrderDetailsModal.tsx
import React from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Button,
  Popconfirm,
  Empty,
  Table,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Notification as OrderNotif } from '../types/Notification';
import { getStatusMeta, typeDesign } from '../ui/design';
import { formatAmPhoneDisplay } from '../utils/phone';
import { formatDateTime } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';

type Props = {
  open: boolean;
  onClose: () => void;
  order: OrderNotif | null | undefined;
  onMarkRead: (ids: string[]) => void;
  onDelete: (ids: string[]) => void;
};

/** Единая строка позиции */
type OrderLine = {
  id: string;
  name: string;
  type: 'original' | 'copy';
  qty: number;
  stock: number;
  price: number;
  total: number;
};

/** детерминированный запас, если его нет в данных */
function fallbackStockFrom(order: OrderNotif, anyId: string | number, idx: number): number {
  const s = `${order.number ?? ''}|${anyId}|${idx}`;
  let h = 0;
  for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) | 0;
  const m = Math.abs(h) % 6;          // 0..5
  if (m === 0) return 0;              // красный
  if (m <= 4) return m;               // 1..4 (жёлтый)
  return 8;                           // зелёный (>=5)
}

/** Нормализуем order.items к OrderLine[] */
function normalizeOrderLines(order: OrderNotif): OrderLine[] {
  const raw: any[] = Array.isArray((order as any).items) ? (order as any).items : [];
  if (raw.length === 0) return [];

  const orderLevelType = ((order as any).itemType ?? '').toString().toLowerCase();
  const inferType = (v: any): 'original' | 'copy' =>
    (v ?? (orderLevelType === 'original' ? 'original' : 'copy')) === 'original' ? 'original' : 'copy';

  return raw.map((it, idx) => {
    const qty = Number(it.quantity ?? it.qty ?? 1);
    const price = Number(it.price ?? 0);
    const total = Number.isFinite(Number(it.total)) ? Number(it.total) : price * qty;

    // ✅ имя: если нет name/title — показываем просто SKU (без «Товар »)
    const name =
      (it.name ?? it.title ?? null) ??
      (it.sku != null ? String(it.sku) : '—');

    // ✅ склад: если нет в данных — генерим детерминированно
    const providedStock = it.stock ?? it.available;
    const stock = Number.isFinite(Number(providedStock))
      ? Number(providedStock)
      : fallbackStockFrom(order, it.sku ?? it.id ?? name, idx);

    return {
      id: it.id ?? `${order.number || 'SO'}-${idx + 1}`,
      name,
      type: inferType(it.type ?? (it.isOriginal ? 'original' : 'copy')),
      qty,
      stock,
      price,
      total,
    };
  });
}

const OrderDetailsModal: React.FC<Props> = ({
  open,
  onClose,
  order,
  onMarkRead,
  onDelete,
}) => {
  if (!order) {
    return (
      <Modal open={open} onCancel={onClose} footer={null} title="Детали заказа" width={720} destroyOnHidden >
        <Empty description="Запись не найдена" />
      </Modal>
    );
  }

  // ✅ подсветка склада: 0 — красный, 1..4 — жёлтый, >=5 — зелёный
  const stockStyle = (stock: number): React.CSSProperties => ({
    color: stock === 0 ? '#cf1322' : stock <= 4 ? '#faad14' : '#389e0d',
    fontWeight: 600,
  });

  const lines = normalizeOrderLines(order);
  const currency = order.currency ?? 'AMD';

  const grandTotal = lines.reduce(
    (acc, l) => acc + (Number.isFinite(l.total) ? l.total : (Number(l.price) || 0) * (Number(l.qty) || 0)),
    0
  );

  const cols: ColumnsType<OrderLine> = [
    { title: 'Товар', dataIndex: 'name' },
    {
      title: 'Тип',
      dataIndex: 'type',
      width: 110,
      render: (v: OrderLine['type']) => {
        const t = typeDesign[v] ?? typeDesign.original;
        return <Tag color={t.color} {...(t.style ? { style: t.style } : {})}>{t.label}</Tag>;
      },
    },
    { title: 'Кол-во', dataIndex: 'qty', width: 90 },
    {
      title: 'Склад',
      dataIndex: 'stock',
      width: 90,
      render: (v: number) => <span style={stockStyle(Number(v) || 0)}>{Number.isFinite(v) ? v : '—'}</span>,
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      width: 120,
      render: (v: number) => formatCurrency(v, currency),
    },
    {
      title: 'Сумма',
      dataIndex: 'total',
      width: 140,
      render: (v: number, r) => formatCurrency(Number.isFinite(v) ? v : r.price * r.qty, currency),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={`Заказ ${order.number}`}
      width={1000}
      destroyOnHidden 
    >
      <Descriptions column={1} bordered size="middle">
        <Descriptions.Item label="Клиент" styles={{ label: { fontWeight: 700 } }}>
          {order.customer}
        </Descriptions.Item>

        <Descriptions.Item label="Создан" styles={{ label: { fontWeight: 700 } }}>
          {formatDateTime(order.createdAt)}
        </Descriptions.Item>


        <Descriptions.Item label="Сумма" styles={{ label: { fontWeight: 700 } }}>
          {formatCurrency(grandTotal, currency)}
        </Descriptions.Item>

        <Descriptions.Item label="Статус" styles={{ label: { fontWeight: 700 } }}>
          {(() => {
            const m = getStatusMeta(order?.status);
            return <Tag color={m.tag.color} {...(m.tag.style ? { style: m.tag.style } : {})}>{m.chip.label}</Tag>;
          })()}
        </Descriptions.Item>

        <Descriptions.Item label="Телефон" styles={{ label: { fontWeight: 700 } }}>
          {formatAmPhoneDisplay(order.phone)}
        </Descriptions.Item>

        <Descriptions.Item label="Заказ" styles={{ label: { fontWeight: 700 } }}>
          <Table<OrderLine>
            rowKey="id"
            size="small"
            columns={cols}
            dataSource={lines}
            pagination={false}
          />
        </Descriptions.Item>
      </Descriptions>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        <Space>
          {!order.isRead && (
            <Button type="primary" ghost onClick={() => onMarkRead([order.id])}>
              Отметить прочитанным
            </Button>
          )}
        </Space>
        <Space>
          <Tag
            color="blue"
            style={{
              height: 32,
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0 12px',
              fontWeight: 700,
              fontSize: 14,
              borderRadius: 6,
              marginRight: 8,
            }}
          >
            <span>Итого: {formatCurrency(grandTotal, currency)}</span>
          </Tag>

          <Popconfirm
            title="Удалить это оповещение?"
            okText="Да"
            cancelText="Отмена"
            onConfirm={() => onDelete([order.id])}
          >
            <Button danger>Удалить</Button>
          </Popconfirm>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      </div>

    </Modal>
  );
};

export default OrderDetailsModal;
